import React, { useState } from "react";
import { Modal, TextInput, Text, Stack, Group } from "@mantine/core";
import { Button } from "@/components/bnp-ui";

interface DeletePropertyModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  propertyId: string;
  propertyAddress?: string;
  loading?: boolean;
  error?: string | null;
}

const DeletePropertyModal: React.FC<DeletePropertyModalProps> = ({
  opened,
  onClose,
  onConfirm,
  propertyId,
  propertyAddress,
  loading = false,
  error = null,
}) => {
  const [confirmationText, setConfirmationText] = useState("");

  const isConfirmationValid = confirmationText === "DELETE";

  const handleConfirm = async () => {
    if (!isConfirmationValid) {
      return;
    }

    await onConfirm();
    setConfirmationText("");
  };

  const handleClose = () => {
    setConfirmationText("");
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Delete Property"
      centered
      size="md"
    >
      <Stack gap="md">
        <div>
          <Text size="sm" fw={500} mb={4}>
            Property ID
          </Text>
          <Text size="sm" c="dimmed">
            {propertyId}
          </Text>
        </div>

        {propertyAddress && (
          <div>
            <Text size="sm" fw={500} mb={4}>
              Address
            </Text>
            <Text size="sm" c="dimmed">
              {propertyAddress}
            </Text>
          </div>
        )}

        <div
          style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "4px",
            padding: "12px",
          }}
        >
          <Text size="sm" fw={600} c="#856404" mb={8}>
            ⚠️ Warning: This action cannot be undone
          </Text>
          <Text size="sm" c="#856404">
            Deleting this property will permanently remove it from the system.
            All associated data will be lost.
          </Text>
        </div>

        <div>
          <Text size="sm" fw={500} mb={8}>
            Type <strong>DELETE</strong> to confirm
          </Text>
          <TextInput
            placeholder="DELETE"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            aria-label="Confirmation input"
            aria-required="true"
            disabled={loading}
          />
        </div>

        {error && (
          <div
            style={{
              backgroundColor: "#f8d7da",
              border: "1px solid #f5c2c7",
              borderRadius: "4px",
              padding: "12px",
            }}
          >
            <Text size="sm" c="#842029">
              {error}
            </Text>
          </div>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={!isConfirmationValid || loading}
          >
            {loading ? "Deleting..." : "Confirm Delete"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default DeletePropertyModal;
